import { z } from "zod";

/**
 * Wire mirrors of shore's `travel-request.dto.ts` and its serializer
 * (HRMS-EXP-020 section 6).
 *
 * TRAVEL HAS NO DRAFT (shore decision 12), which is the one shape difference
 * from a claim and the reason this file has no `submit` anywhere: a request
 * exists only once it is with an approver. A claim can be built up over a trip;
 * authorization is asked for once, before it.
 *
 * A cost line carries no currency of its own. The request carries one and every
 * line is in it, so a per-line code would be a second source of truth that
 * nothing sums across.
 */

const MONEY = /^\d{1,13}(\.\d{1,2})?$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const TRAVEL_TYPES = ["domestic", "international"] as const;
export type TravelType = (typeof TRAVEL_TYPES)[number];

export const TRAVEL_MODES = ["flight", "train", "bus", "car", "other"] as const;
export type TravelMode = (typeof TRAVEL_MODES)[number];

export const TRAVEL_STATUSES = ["pending", "approved", "rejected", "cancelled"] as const;
export type TravelStatus = (typeof TRAVEL_STATUSES)[number];

export const TRAVEL_SCOPES = ["mine", "team", "workspace"] as const;
export type TravelScope = (typeof TRAVEL_SCOPES)[number];

export const travelRequestRowSchema = z
  .object({
    id: z.string(),
    membershipId: z.string(),
    memberName: z.string(),
    travelType: z.string(),
    purpose: z.string(),
    status: z.string(),
    currency: z.string(),
    estimatedCost: z.string(),
    legCount: z.number().int(),
    createdAt: z.string(),
    version: z.number().int(),
  })
  .strict();
export type TravelRequestRow = z.infer<typeof travelRequestRowSchema>;

export const travelRequestListSchema = z
  .object({ rows: z.array(travelRequestRowSchema), nextCursor: z.string().nullable() })
  .strict();
export type TravelRequestList = z.infer<typeof travelRequestListSchema>;

export const travelLegSchema = z
  .object({
    id: z.string(),
    fromPlace: z.string(),
    toPlace: z.string(),
    departOn: z.string(),
    arriveOn: z.string().nullable(),
    mode: z.string().nullable(),
    lodgingRequired: z.boolean(),
    notes: z.string().nullable(),
  })
  .strict();
export type TravelLeg = z.infer<typeof travelLegSchema>;

export const travelCostLineSchema = z
  .object({ id: z.string(), description: z.string(), amount: z.string() })
  .strict();
export type TravelCostLine = z.infer<typeof travelCostLineSchema>;

export const travelAttachmentSchema = z
  .object({
    id: z.string(),
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number(),
    createdAt: z.string(),
  })
  .strict();

/// The optional half is populated only once a decision exists. Optional rather
/// than nullable because a pending request has not been decided by nobody: the
/// fields do not apply yet, and an absent key says that where `null` would
/// invite an empty decider on screen.
export const travelApprovalSchema = z
  .object({
    approvalRequestId: z.string(),
    status: z.string(),
    resolvedVia: z.string(),
    decidedByMembershipId: z.string().optional(),
    decidedByName: z.string().optional(),
    decidedAt: z.string().optional(),
    decisionReason: z.string().optional(),
  })
  .strict();

export const travelRequestDetailSchema = travelRequestRowSchema
  .extend({
    itinerary: z.array(travelLegSchema),
    costLines: z.array(travelCostLineSchema),
    attachments: z.array(travelAttachmentSchema),
    approval: travelApprovalSchema.nullable(),
  })
  .strict();
export type TravelRequestDetail = z.infer<typeof travelRequestDetailSchema>;

export const travelLegInputSchema = z
  .object({
    fromPlace: z.string().trim().min(1, "Where from?").max(120),
    toPlace: z.string().trim().min(1, "Where to?").max(120),
    departOn: z.string().regex(ISO_DATE, "Use a date in YYYY-MM-DD format."),
    arriveOn: z.string().regex(ISO_DATE).optional(),
    mode: z.enum(TRAVEL_MODES).optional(),
    lodgingRequired: z.boolean().optional(),
    notes: z.string().max(500).optional(),
  })
  .strict()
  .superRefine((leg, ctx) => {
    if (leg.arriveOn !== undefined && leg.arriveOn < leg.departOn) {
      ctx.addIssue({
        code: "custom",
        path: ["arriveOn"],
        message: "Arrival cannot be before departure.",
      });
    }
  });
export type TravelLegInput = z.infer<typeof travelLegInputSchema>;

export const travelCostLineInputSchema = z
  .object({
    description: z.string().trim().min(1).max(120),
    amount: z
      .string()
      .regex(MONEY, "Use an amount with at most 2 decimal places.")
      .refine((value) => Number(value) > 0, "The amount must be more than zero."),
  })
  .strict();
export type TravelCostLineInput = z.infer<typeof travelCostLineInputSchema>;

export const createTravelRequestSchema = z
  .object({
    travelType: z.enum(TRAVEL_TYPES),
    purpose: z.string().trim().min(1, "Say why the trip is needed.").max(200),
    currency: z.string().regex(/^[A-Z]{3}$/),
    itinerary: z.array(travelLegInputSchema).min(1).max(10),
    costLines: z.array(travelCostLineInputSchema).max(15).optional(),
    attachmentIds: z.array(z.string()).max(5).optional(),
  })
  .strict();
export type CreateTravelRequest = z.infer<typeof createTravelRequestSchema>;

/// Replace-all PATCH, reachable only while the request is still pending. Once
/// an approver has decided it this 409s, which is deliberate: an editable
/// decided request would let a traveller change what was authorized.
export const replaceTravelRequestSchema = z
  .object({
    version: z.number().int().min(0),
    travelType: z.enum(TRAVEL_TYPES).optional(),
    purpose: z.string().trim().min(1).max(200).optional(),
    itinerary: z.array(travelLegInputSchema).min(1).max(10).optional(),
    costLines: z.array(travelCostLineInputSchema).max(15).optional(),
    attachmentIds: z.array(z.string()).max(5).optional(),
  })
  .strict();
export type ReplaceTravelRequest = z.infer<typeof replaceTravelRequestSchema>;

export const cancelTravelRequestSchema = z
  .object({
    version: z.number().int().min(0),
    reason: z.string().trim().min(3, "Say why the trip is off.").max(500),
  })
  .strict();
export type CancelTravelRequest = z.infer<typeof cancelTravelRequestSchema>;

export const listTravelRequestsQuerySchema = z
  .object({
    scope: z.enum(TRAVEL_SCOPES).optional(),
    status: z.enum(TRAVEL_STATUSES).optional(),
    membershipId: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(100).optional(),
  })
  .strict();
export type ListTravelRequestsQuery = z.infer<typeof listTravelRequestsQuerySchema>;
